/**
 * Provider catalogue — mirrors python_tools/app/services/pipecat/config.py
 *
 * Embedded here so the UI never depends on Python tools being reachable.
 * Keep in sync with config.py when adding/removing providers or fields.
 *
 * Field types:
 *   select          — dropdown from static options list
 *   select_from_api — dropdown populated at runtime from an external API
 *   float           — slider + numeric display (min/max/step)
 *   int             — integer input (min/max)
 *   boolean         — toggle
 *
 * language_dependent: true  → options are filtered by language_map[langCode]
 */

// ── LLM Providers ─────────────────────────────────────────────────────────────

export const LLM_PROVIDERS = {
  openai: {
    label: "OpenAI",
    default_model: "gpt-4.1",
    fields: [
      {
        key: "model",
        label: "Model",
        type: "select",
        required: true,
        default: "gpt-4.1",
        options: [
          { value: "gpt-5",         label: "GPT-5 (latest flagship)" },
          { value: "gpt-4.1",       label: "GPT-4.1" },
          { value: "gpt-4.1-mini",  label: "GPT-4.1 Mini" },
          { value: "gpt-4.1-nano",  label: "GPT-4.1 Nano" },
          { value: "gpt-4o",        label: "GPT-4o" },
          { value: "gpt-4o-mini",   label: "GPT-4o Mini" },
          { value: "o3",            label: "o3 (reasoning)" },
          { value: "o4-mini",       label: "o4 Mini (reasoning)" },
        ],
      },
      {
        key: "temperature",
        label: "Temperature",
        type: "float",
        required: false,
        default: 0.7,
        min: 0.0,
        max: 2.0,
        step: 0.1,
        description: "Controls randomness. Lower = more deterministic.",
      },
      {
        key: "max_tokens",
        label: "Max Tokens",
        type: "int",
        required: false,
        default: 150,
        min: 50,
        max: 4096,
        description: "Maximum tokens per response turn.",
      },
    ],
  },

  anthropic: {
    label: "Anthropic",
    default_model: "claude-sonnet-4-6",
    fields: [
      {
        key: "model",
        label: "Model",
        type: "select",
        required: true,
        default: "claude-sonnet-4-6",
        options: [
          { value: "claude-opus-4-6",          label: "Claude Opus 4.6" },
          { value: "claude-sonnet-4-6",         label: "Claude Sonnet 4.6" },
          { value: "claude-haiku-4-5-20251001", label: "Claude Haiku 4.5" },
        ],
      },
      {
        key: "temperature",
        label: "Temperature",
        type: "float",
        required: false,
        default: 0.7,
        min: 0.0,
        max: 1.0,
        step: 0.1,
      },
      {
        key: "max_tokens",
        label: "Max Tokens",
        type: "int",
        required: false,
        default: 150,
        min: 50,
        max: 8192,
      },
    ],
  },

  gemini: {
    label: "Google Gemini",
    default_model: "gemini-2.5-flash",
    fields: [
      {
        key: "model",
        label: "Model",
        type: "select",
        required: true,
        default: "gemini-2.5-flash",
        options: [
          { value: "gemini-2.5-pro",        label: "Gemini 2.5 Pro (latest stable)" },
          { value: "gemini-2.5-flash",       label: "Gemini 2.5 Flash" },
          { value: "gemini-2.5-flash-lite",  label: "Gemini 2.5 Flash-Lite" },
          { value: "gemini-2.0-flash",       label: "Gemini 2.0 Flash (deprecated Jun 2026)" },
          { value: "gemini-1.5-pro",         label: "Gemini 1.5 Pro" },
          { value: "gemini-1.5-flash",       label: "Gemini 1.5 Flash" },
        ],
      },
      {
        key: "temperature",
        label: "Temperature",
        type: "float",
        required: false,
        default: 0.7,
        min: 0.0,
        max: 1.0,
        step: 0.1,
      },
      {
        key: "max_tokens",
        label: "Max Output Tokens",
        type: "int",
        required: false,
        default: 150,
        min: 50,
        max: 8192,
      },
      {
        key: "top_p",
        label: "Top P",
        type: "float",
        required: false,
        default: 0.95,
        min: 0.0,
        max: 1.0,
        step: 0.01,
        description: "Nucleus sampling probability.",
      },
    ],
  },
};

// ── API key env var hints (shown in the UI) ───────────────────────────────────
export const LLM_API_KEY_HINTS = {
  openai:    { envVar: "OPENAI_API_KEY",    label: "OpenAI API Key" },
  anthropic: { envVar: "ANTHROPIC_API_KEY", label: "Anthropic API Key" },
  gemini:    { envVar: "GOOGLE_API_KEY",    label: "Google AI API Key" },
};

export const TTS_API_KEY_HINTS = {
  elevenlabs:  { envVar: "ELEVENLABS_API_KEY",  label: "ElevenLabs API Key" },
  cartesia:    { envVar: "CARTESIA_API_KEY",    label: "Cartesia API Key" },
  sarvam:      { envVar: "SARVAM_API_KEY",      label: "Sarvam API Key" },
  deepgram:    { envVar: "DEEPGRAM_API_KEY",    label: "Deepgram API Key" },
  simplismart: { envVar: "SIMPLISMART_API_KEY", label: "Simplismart API Key (JWT)" },
  // openai and azure reuse keys already set via LLM or telephony config
};

export const ASR_API_KEY_HINTS = {
  deepgram:   { envVar: "DEEPGRAM_API_KEY",   label: "Deepgram API Key" },
  assemblyai: { envVar: "ASSEMBLYAI_API_KEY", label: "AssemblyAI API Key" },
  sarvam:     { envVar: "SARVAM_API_KEY",     label: "Sarvam API Key" },
  // openai reuses OPENAI_API_KEY already set in LLM config
};

// ── TTS Providers ──────────────────────────────────────────────────────────────

export const TTS_PROVIDERS = {
  deepgram: {
    label: "Deepgram Aura",
    fields: [
      {
        key: "voice",
        label: "Voice",
        type: "select",
        required: true,
        default: "aura-2-thalia-en",
        options: [
          { value: "aura-2-thalia-en", label: "Thalia (Aura-2, female, US)" },
          { value: "aura-2-andromeda-en", label: "Andromeda (Aura-2, female, US)" },
          { value: "aura-2-helena-en", label: "Helena (Aura-2, female, US)" },
          { value: "aura-2-apollo-en", label: "Apollo (Aura-2, male, US)" },
          { value: "aura-2-arcas-en", label: "Arcas (Aura-2, male, US)" },
          { value: "aura-asteria-en", label: "Asteria (Aura-1, female, US)" },
          { value: "aura-luna-en", label: "Luna (Aura-1, female, US)" },
          { value: "aura-orion-en", label: "Orion (Aura-1, male, US)" },
          { value: "aura-zeus-en", label: "Zeus (Aura-1, male, US)" },
        ],
      },
    ],
  },
  openai: {
    label: "OpenAI TTS",
    fields: [
      {
        key: "model",
        label: "Model",
        type: "select",
        required: true,
        default: "gpt-4o-mini-tts",
        // Docs: https://platform.openai.com/docs/models#text-to-speech
        options: [
          { value: "gpt-4o-mini-tts", label: "GPT-4o Mini TTS (latest, steerable)" },
          { value: "gpt-4o-mini-tts-2025-12-15", label: "GPT-4o Mini TTS (2025-12-15 snapshot)" },
          { value: "tts-1",           label: "TTS-1 (faster)" },
          { value: "tts-1-hd",        label: "TTS-1 HD (higher quality)" },
        ],
      },
      {
        key: "voice",
        label: "Voice",
        type: "select",
        required: true,
        default: "alloy",
        language_dependent: true,
        options: [
          { value: "alloy",   label: "Alloy (neutral)" },
          { value: "echo",    label: "Echo (male)" },
          { value: "fable",   label: "Fable (male, British)" },
          { value: "onyx",    label: "Onyx (male, deep)" },
          { value: "nova",    label: "Nova (female)" },
          { value: "shimmer", label: "Shimmer (female, soft)" },
          { value: "ash",     label: "Ash (male)" },
          { value: "coral",   label: "Coral (female, warm)" },
          { value: "sage",    label: "Sage (female, calm)" },
          { value: "ballad",  label: "Ballad (male, expressive — GPT-4o Mini TTS only)" },
          { value: "verse",   label: "Verse (male — GPT-4o Mini TTS only)" },
          { value: "marin",   label: "Marin (female — GPT-4o Mini TTS only)" },
          { value: "cedar",   label: "Cedar (male — GPT-4o Mini TTS only)" },
        ],
        // All voices support all languages
        language_map: null,
      },
      {
        key: "speed",
        label: "Speed",
        type: "float",
        required: false,
        default: 1.0,
        min: 0.25,
        max: 4.0,
        step: 0.05,
        description: "Playback speed multiplier.",
      },
    ],
  },

  elevenlabs: {
    label: "ElevenLabs",
    fields: [
      {
        key: "model",
        label: "Model",
        type: "select",
        required: true,
        default: "eleven_flash_v2_5",
        language_dependent: true,
        // Docs: https://elevenlabs.io/docs/models
        options: [
          { value: "eleven_flash_v2_5",      label: "Flash v2.5 (fastest, ~75 ms, multilingual)" },
          { value: "eleven_turbo_v2_5",      label: "Turbo v2.5 (low latency, multilingual)" },
          { value: "eleven_multilingual_v2", label: "Multilingual v2 (highest quality)" },
          { value: "eleven_monolingual_v1",  label: "Monolingual v1 (English only)" },
          { value: "eleven_turbo_v2",        label: "Turbo v2 (English only)" },
        ],
        language_map: {
          en: ["eleven_flash_v2_5", "eleven_turbo_v2_5", "eleven_multilingual_v2", "eleven_monolingual_v1", "eleven_turbo_v2"],
          _default: ["eleven_flash_v2_5", "eleven_turbo_v2_5", "eleven_multilingual_v2"],
        },
      },
      {
        key: "voice_id",
        label: "Voice",
        type: "select_from_api",
        required: true,
        default: null,
        api_source: "elevenlabs_voices",
        description: "Voice fetched from your ElevenLabs account.",
      },
      {
        key: "stability",
        label: "Stability",
        type: "float",
        required: false,
        default: 0.5,
        min: 0.0,
        max: 1.0,
        step: 0.05,
        description: "Higher = more consistent. Lower = more expressive.",
      },
      {
        key: "similarity_boost",
        label: "Clarity / Similarity",
        type: "float",
        required: false,
        default: 0.75,
        min: 0.0,
        max: 1.0,
        step: 0.05,
      },
      {
        key: "style",
        label: "Style Exaggeration",
        type: "float",
        required: false,
        default: 0.0,
        min: 0.0,
        max: 1.0,
        step: 0.05,
      },
      {
        key: "use_speaker_boost",
        label: "Speaker Boost",
        type: "boolean",
        required: false,
        default: true,
        description: "Boosts similarity to the original voice.",
      },
    ],
  },

  azure: {
    label: "Azure Cognitive Speech",
    fields: [
      {
        key: "voice",
        label: "Voice",
        type: "select",
        required: true,
        default: "en-US-AvaMultilingualNeural",
        language_dependent: true,
        options: [
          // Generated from the voice_providers DB (Azure). Covers all primary
          // languages; language_map filters by the selected language's 2-letter code.
          { value: "af-ZA-AdriNeural", label: "Adri (af-ZA)" },
          { value: "af-ZA-WillemNeural", label: "Willem (af-ZA)" },
          { value: "ar-AE-FatimaNeural", label: "Fatima (ar-AE)" },
          { value: "ar-AE-HamdanNeural", label: "Hamdan (ar-AE)" },
          { value: "ar-BH-AliNeural", label: "Ali (ar-BH)" },
          { value: "ar-BH-LailaNeural", label: "Laila (ar-BH)" },
          { value: "ar-DZ-AminaNeural", label: "Amina (ar-DZ)" },
          { value: "ar-DZ-IsmaelNeural", label: "Ismael (ar-DZ)" },
          { value: "ar-EG-SalmaNeural", label: "Salma (ar-EG)" },
          { value: "ar-EG-ShakirNeural", label: "Shakir (ar-EG)" },
          { value: "ar-IQ-BasselNeural", label: "Bassel (ar-IQ)" },
          { value: "ar-IQ-RanaNeural", label: "Rana (ar-IQ)" },
          { value: "ar-JO-SanaNeural", label: "Sana (ar-JO)" },
          { value: "ar-JO-TaimNeural", label: "Taim (ar-JO)" },
          { value: "ar-KW-FahedNeural", label: "Fahed (ar-KW)" },
          { value: "ar-KW-NouraNeural", label: "Noura (ar-KW)" },
          { value: "ar-LB-LaylaNeural", label: "Layla (ar-LB)" },
          { value: "ar-LB-RamiNeural", label: "Rami (ar-LB)" },
          { value: "ar-LY-ImanNeural", label: "Iman (ar-LY)" },
          { value: "ar-LY-OmarNeural", label: "Omar (ar-LY)" },
          { value: "bg-BG-BorislavNeural", label: "Borislav (bg-BG)" },
          { value: "bg-BG-KalinaNeural", label: "Kalina (bg-BG)" },
          { value: "bn-IN-BashkarNeural", label: "Bashkar (bn-IN)" },
          { value: "bn-IN-TanishaaNeural", label: "Tanishaa (bn-IN)" },
          { value: "ca-ES-AlbaNeural", label: "Alba (ca-ES)" },
          { value: "ca-ES-EnricNeural", label: "Enric (ca-ES)" },
          { value: "ca-ES-JoanaNeural", label: "Joana (ca-ES)" },
          { value: "cs-CZ-AntoninNeural", label: "Antonin (cs-CZ)" },
          { value: "cs-CZ-VlastaNeural", label: "Vlasta (cs-CZ)" },
          { value: "da-DK-ChristelNeural", label: "Christel (da-DK)" },
          { value: "da-DK-JeppeNeural", label: "Jeppe (da-DK)" },
          { value: "de-DE-ConradNeural", label: "Conrad (de-DE)" },
          { value: "de-DE-KatjaNeural", label: "Katja (de-DE)" },
          { value: "de-DE-SeraphinaMultilingualNeural", label: "Seraphina (de-DE)" },
          { value: "el-GR-AthinaNeural", label: "Athina (el-GR)" },
          { value: "el-GR-NestorasNeural", label: "Nestoras (el-GR)" },
          { value: "en-AU-AnnetteNeural", label: "Annette (en-AU)" },
          { value: "en-AU-NatashaNeural", label: "Natasha (en-AU)" },
          { value: "en-AU-WilliamNeural", label: "William (en-AU)" },
          { value: "en-GB-LibbyNeural", label: "Libby (en-GB)" },
          { value: "en-GB-RyanNeural", label: "Ryan (en-GB)" },
          { value: "en-GB-SoniaNeural", label: "Sonia (en-GB)" },
          { value: "en-IN-AaravNeural", label: "Aarav (en-IN)" },
          { value: "en-IN-AartiNeural", label: "Aarti (en-IN)" },
          { value: "en-IN-AashiNeural", label: "Aashi (en-IN)" },
          { value: "en-US-AndrewMultilingualNeural", label: "Andrew (en-US)" },
          { value: "en-US-AvaMultilingualNeural", label: "Ava (en-US)" },
          { value: "en-US-EmmaMultilingualNeural", label: "Emma (en-US)" },
          { value: "es-ES-AlvaroNeural", label: "Alvaro (es-ES)" },
          { value: "es-ES-ArabellaMultilingualNeural", label: "Arabella (es-ES)" },
          { value: "es-ES-ElviraNeural", label: "Elvira (es-ES)" },
          { value: "es-US-AlonsoNeural", label: "Alonso (es-US)" },
          { value: "es-US-PalomaNeural", label: "Paloma (es-US)" },
          { value: "et-EE-AnuNeural", label: "Anu (et-EE)" },
          { value: "et-EE-KertNeural", label: "Kert (et-EE)" },
          { value: "eu-ES-AinhoaNeural", label: "Ainhoa (eu-ES)" },
          { value: "eu-ES-AnderNeural", label: "Ander (eu-ES)" },
          { value: "fi-FI-HarriNeural", label: "Harri (fi-FI)" },
          { value: "fi-FI-NooraNeural", label: "Noora (fi-FI)" },
          { value: "fi-FI-SelmaNeural", label: "Selma (fi-FI)" },
          { value: "fil-PH-AngeloNeural", label: "Angelo (fil-PH)" },
          { value: "fil-PH-BlessicaNeural", label: "Blessica (fil-PH)" },
          { value: "fr-CA-AntoineNeural", label: "Antoine (fr-CA)" },
          { value: "fr-CA-JeanNeural", label: "Jean (fr-CA)" },
          { value: "fr-CA-SylvieNeural", label: "Sylvie (fr-CA)" },
          { value: "fr-FR-DeniseNeural", label: "Denise (fr-FR)" },
          { value: "fr-FR-HenriNeural", label: "Henri (fr-FR)" },
          { value: "fr-FR-VivienneMultilingualNeural", label: "Vivienne (fr-FR)" },
          { value: "gl-ES-RoiNeural", label: "Roi (gl-ES)" },
          { value: "gl-ES-SabelaNeural", label: "Sabela (gl-ES)" },
          { value: "gu-IN-DhwaniNeural", label: "Dhwani (gu-IN)" },
          { value: "gu-IN-NiranjanNeural", label: "Niranjan (gu-IN)" },
          { value: "he-IL-AvriNeural", label: "Avri (he-IL)" },
          { value: "he-IL-HilaNeural", label: "Hila (he-IL)" },
          { value: "hi-IN-AaravNeural", label: "Aarav (hi-IN)" },
          { value: "hi-IN-AartiNeural", label: "Aarti (hi-IN)" },
          { value: "hi-IN-AnanyaNeural", label: "Ananya (hi-IN)" },
          { value: "hu-HU-NoemiNeural", label: "Noemi (hu-HU)" },
          { value: "hu-HU-TamasNeural", label: "Tamas (hu-HU)" },
          { value: "id-ID-ArdiNeural", label: "Ardi (id-ID)" },
          { value: "id-ID-GadisNeural", label: "Gadis (id-ID)" },
          { value: "is-IS-GudrunNeural", label: "Gudrun (is-IS)" },
          { value: "is-IS-GunnarNeural", label: "Gunnar (is-IS)" },
          { value: "it-IT-DiegoNeural", label: "Diego (it-IT)" },
          { value: "it-IT-ElsaNeural", label: "Elsa (it-IT)" },
          { value: "it-IT-IsabellaNeural", label: "Isabella (it-IT)" },
          { value: "ja-JP-AoiNeural", label: "Aoi (ja-JP)" },
          { value: "ja-JP-KeitaNeural", label: "Keita (ja-JP)" },
          { value: "ja-JP-NanamiNeural", label: "Nanami (ja-JP)" },
          { value: "kn-IN-GaganNeural", label: "Gagan (kn-IN)" },
          { value: "kn-IN-SapnaNeural", label: "Sapna (kn-IN)" },
          { value: "ko-KR-HyunsuMultilingualNeural", label: "Hyunsu (ko-KR)" },
          { value: "ko-KR-InJoonNeural", label: "InJoon (ko-KR)" },
          { value: "ko-KR-SunHiNeural", label: "SunHi (ko-KR)" },
          { value: "lt-LT-LeonasNeural", label: "Leonas (lt-LT)" },
          { value: "lt-LT-OnaNeural", label: "Ona (lt-LT)" },
          { value: "lv-LV-EveritaNeural", label: "Everita (lv-LV)" },
          { value: "lv-LV-NilsNeural", label: "Nils (lv-LV)" },
          { value: "ml-IN-MidhunNeural", label: "Midhun (ml-IN)" },
          { value: "ml-IN-SobhanaNeural", label: "Sobhana (ml-IN)" },
          { value: "mr-IN-AarohiNeural", label: "Aarohi (mr-IN)" },
          { value: "mr-IN-ManoharNeural", label: "Manohar (mr-IN)" },
          { value: "ms-MY-OsmanNeural", label: "Osman (ms-MY)" },
          { value: "ms-MY-YasminNeural", label: "Yasmin (ms-MY)" },
          { value: "nb-NO-FinnNeural", label: "Finn (nb-NO)" },
          { value: "nb-NO-IselinNeural", label: "Iselin (nb-NO)" },
          { value: "nb-NO-PernilleNeural", label: "Pernille (nb-NO)" },
          { value: "nl-BE-ArnaudNeural", label: "Arnaud (nl-BE)" },
          { value: "nl-BE-DenaNeural", label: "Dena (nl-BE)" },
          { value: "nl-NL-ColetteNeural", label: "Colette (nl-NL)" },
          { value: "nl-NL-FennaNeural", label: "Fenna (nl-NL)" },
          { value: "nl-NL-MaartenNeural", label: "Maarten (nl-NL)" },
          { value: "pa-IN-OjasNeural", label: "Ojas (pa-IN)" },
          { value: "pa-IN-VaaniNeural", label: "Vaani (pa-IN)" },
          { value: "pl-PL-AgnieszkaNeural", label: "Agnieszka (pl-PL)" },
          { value: "pl-PL-MarekNeural", label: "Marek (pl-PL)" },
          { value: "pl-PL-ZofiaNeural", label: "Zofia (pl-PL)" },
          { value: "pt-BR-AntonioNeural", label: "Antonio (pt-BR)" },
          { value: "pt-BR-FranciscaNeural", label: "Francisca (pt-BR)" },
          { value: "pt-BR-MacerioMultilingualNeural", label: "Macerio (pt-BR)" },
          { value: "pt-PT-DuarteNeural", label: "Duarte (pt-PT)" },
          { value: "pt-PT-FernandaNeural", label: "Fernanda (pt-PT)" },
          { value: "pt-PT-RaquelNeural", label: "Raquel (pt-PT)" },
          { value: "ro-RO-AlinaNeural", label: "Alina (ro-RO)" },
          { value: "ro-RO-EmilNeural", label: "Emil (ro-RO)" },
          { value: "ru-RU-DariyaNeural", label: "Dariya (ru-RU)" },
          { value: "ru-RU-DmitryNeural", label: "Dmitry (ru-RU)" },
          { value: "ru-RU-SvetlanaNeural", label: "Svetlana (ru-RU)" },
          { value: "sk-SK-LukasNeural", label: "Lukas (sk-SK)" },
          { value: "sk-SK-ViktoriaNeural", label: "Viktoria (sk-SK)" },
          { value: "sr-RS-NicholasNeural", label: "Nicholas (sr-RS)" },
          { value: "sr-RS-SophieNeural", label: "Sophie (sr-RS)" },
          { value: "sv-SE-HilleviNeural", label: "Hillevi (sv-SE)" },
          { value: "sv-SE-MattiasNeural", label: "Mattias (sv-SE)" },
          { value: "sv-SE-SofieNeural", label: "Sofie (sv-SE)" },
          { value: "ta-IN-PallaviNeural", label: "Pallavi (ta-IN)" },
          { value: "ta-IN-ValluvarNeural", label: "Valluvar (ta-IN)" },
          { value: "te-IN-MohanNeural", label: "Mohan (te-IN)" },
          { value: "te-IN-ShrutiNeural", label: "Shruti (te-IN)" },
          { value: "th-TH-AcharaNeural", label: "Achara (th-TH)" },
          { value: "th-TH-NiwatNeural", label: "Niwat (th-TH)" },
          { value: "th-TH-PremwadeeNeural", label: "Premwadee (th-TH)" },
          { value: "tr-TR-AhmetNeural", label: "Ahmet (tr-TR)" },
          { value: "tr-TR-EmelNeural", label: "Emel (tr-TR)" },
          { value: "uk-UA-OstapNeural", label: "Ostap (uk-UA)" },
          { value: "uk-UA-PolinaNeural", label: "Polina (uk-UA)" },
          { value: "ur-IN-GulNeural", label: "Gul (ur-IN)" },
          { value: "ur-IN-SalmanNeural", label: "Salman (ur-IN)" },
          { value: "vi-VN-HoaiMyNeural", label: "HoaiMy (vi-VN)" },
          { value: "vi-VN-NamMinhNeural", label: "NamMinh (vi-VN)" },
          { value: "zh-CN-XiaoxiaoNeural", label: "Xiaoxiao (zh-CN)" },
          { value: "zh-CN-YunjianNeural", label: "Yunjian (zh-CN)" },
          { value: "zh-CN-YunxiNeural", label: "Yunxi (zh-CN)" },
          { value: "zh-CN-guangxi-YunqiNeural", label: "Yunqi (zh-CN-guangxi)" },
          { value: "zh-CN-henan-YundengNeural", label: "Yundeng (zh-CN-henan)" },
          { value: "zh-CN-liaoning-XiaobeiNeural", label: "Xiaobei (zh-CN-liaoning)" },
          { value: "zh-CN-liaoning-YunbiaoNeural", label: "Yunbiao (zh-CN-liaoning)" },
          { value: "zh-CN-shaanxi-XiaoniNeural", label: "Xiaoni (zh-CN-shaanxi)" },
          { value: "zh-CN-shandong-YunxiangNeural", label: "Yunxiang (zh-CN-shandong)" },
          { value: "zh-CN-sichuan-YunxiNeural", label: "Yunxi (zh-CN-sichuan)" },
          { value: "zh-HK-HiuGaaiNeural", label: "HiuGaai (zh-HK)" },
          { value: "zh-HK-HiuMaanNeural", label: "HiuMaan (zh-HK)" },
          { value: "zh-HK-WanLungNeural", label: "WanLung (zh-HK)" },
          { value: "zh-TW-HsiaoChenNeural", label: "HsiaoChen (zh-TW)" },
          { value: "zh-TW-HsiaoYuNeural", label: "HsiaoYu (zh-TW)" },
          { value: "zh-TW-YunJheNeural", label: "YunJhe (zh-TW)" },
        ],
        language_map: {
          af: ["af-ZA-AdriNeural", "af-ZA-WillemNeural"],
          ar: ["ar-AE-FatimaNeural", "ar-AE-HamdanNeural", "ar-BH-LailaNeural", "ar-BH-AliNeural", "ar-DZ-AminaNeural", "ar-DZ-IsmaelNeural", "ar-EG-SalmaNeural", "ar-EG-ShakirNeural", "ar-IQ-RanaNeural", "ar-IQ-BasselNeural", "ar-JO-SanaNeural", "ar-JO-TaimNeural", "ar-KW-NouraNeural", "ar-KW-FahedNeural", "ar-LB-LaylaNeural", "ar-LB-RamiNeural", "ar-LY-ImanNeural", "ar-LY-OmarNeural"],
          bg: ["bg-BG-KalinaNeural", "bg-BG-BorislavNeural"],
          bn: ["bn-IN-TanishaaNeural", "bn-IN-BashkarNeural"],
          ca: ["ca-ES-JoanaNeural", "ca-ES-EnricNeural", "ca-ES-AlbaNeural"],
          cmn: ["zh-CN-XiaoxiaoNeural", "zh-CN-YunxiNeural", "zh-CN-YunjianNeural", "zh-CN-guangxi-YunqiNeural", "zh-CN-henan-YundengNeural", "zh-CN-liaoning-XiaobeiNeural", "zh-CN-liaoning-YunbiaoNeural", "zh-CN-shaanxi-XiaoniNeural", "zh-CN-shandong-YunxiangNeural", "zh-CN-sichuan-YunxiNeural", "zh-TW-HsiaoChenNeural", "zh-TW-YunJheNeural", "zh-TW-HsiaoYuNeural", "zh-HK-HiuMaanNeural", "zh-HK-WanLungNeural", "zh-HK-HiuGaaiNeural"],
          cs: ["cs-CZ-VlastaNeural", "cs-CZ-AntoninNeural"],
          da: ["da-DK-ChristelNeural", "da-DK-JeppeNeural"],
          de: ["de-DE-KatjaNeural", "de-DE-ConradNeural", "de-DE-SeraphinaMultilingualNeural"],
          el: ["el-GR-AthinaNeural", "el-GR-NestorasNeural"],
          en: ["en-AU-NatashaNeural", "en-AU-WilliamNeural", "en-AU-AnnetteNeural", "en-GB-SoniaNeural", "en-GB-RyanNeural", "en-GB-LibbyNeural", "en-IN-AaravNeural", "en-IN-AashiNeural", "en-IN-AartiNeural", "en-US-AvaMultilingualNeural", "en-US-AndrewMultilingualNeural", "en-US-EmmaMultilingualNeural"],
          es: ["es-ES-ElviraNeural", "es-ES-AlvaroNeural", "es-ES-ArabellaMultilingualNeural", "es-US-PalomaNeural", "es-US-AlonsoNeural"],
          et: ["et-EE-AnuNeural", "et-EE-KertNeural"],
          eu: ["eu-ES-AinhoaNeural", "eu-ES-AnderNeural"],
          fi: ["fi-FI-SelmaNeural", "fi-FI-HarriNeural", "fi-FI-NooraNeural"],
          fil: ["fil-PH-BlessicaNeural", "fil-PH-AngeloNeural"],
          fr: ["fr-CA-SylvieNeural", "fr-CA-JeanNeural", "fr-CA-AntoineNeural", "fr-FR-DeniseNeural", "fr-FR-HenriNeural", "fr-FR-VivienneMultilingualNeural"],
          gl: ["gl-ES-SabelaNeural", "gl-ES-RoiNeural"],
          gu: ["gu-IN-DhwaniNeural", "gu-IN-NiranjanNeural"],
          he: ["he-IL-HilaNeural", "he-IL-AvriNeural"],
          hi: ["hi-IN-AaravNeural", "hi-IN-AnanyaNeural", "hi-IN-AartiNeural"],
          hu: ["hu-HU-NoemiNeural", "hu-HU-TamasNeural"],
          id: ["id-ID-GadisNeural", "id-ID-ArdiNeural"],
          is: ["is-IS-GudrunNeural", "is-IS-GunnarNeural"],
          it: ["it-IT-ElsaNeural", "it-IT-IsabellaNeural", "it-IT-DiegoNeural"],
          ja: ["ja-JP-NanamiNeural", "ja-JP-KeitaNeural", "ja-JP-AoiNeural"],
          kn: ["kn-IN-SapnaNeural", "kn-IN-GaganNeural"],
          ko: ["ko-KR-SunHiNeural", "ko-KR-InJoonNeural", "ko-KR-HyunsuMultilingualNeural"],
          lt: ["lt-LT-OnaNeural", "lt-LT-LeonasNeural"],
          lv: ["lv-LV-EveritaNeural", "lv-LV-NilsNeural"],
          ml: ["ml-IN-SobhanaNeural", "ml-IN-MidhunNeural"],
          mr: ["mr-IN-AarohiNeural", "mr-IN-ManoharNeural"],
          ms: ["ms-MY-YasminNeural", "ms-MY-OsmanNeural"],
          nb: ["nb-NO-PernilleNeural", "nb-NO-FinnNeural", "nb-NO-IselinNeural"],
          nl: ["nl-BE-DenaNeural", "nl-BE-ArnaudNeural", "nl-NL-FennaNeural", "nl-NL-MaartenNeural", "nl-NL-ColetteNeural"],
          pa: ["pa-IN-OjasNeural", "pa-IN-VaaniNeural"],
          pl: ["pl-PL-AgnieszkaNeural", "pl-PL-MarekNeural", "pl-PL-ZofiaNeural"],
          pt: ["pt-BR-FranciscaNeural", "pt-BR-AntonioNeural", "pt-BR-MacerioMultilingualNeural", "pt-PT-RaquelNeural", "pt-PT-DuarteNeural", "pt-PT-FernandaNeural"],
          ro: ["ro-RO-AlinaNeural", "ro-RO-EmilNeural"],
          ru: ["ru-RU-SvetlanaNeural", "ru-RU-DmitryNeural", "ru-RU-DariyaNeural"],
          sk: ["sk-SK-ViktoriaNeural", "sk-SK-LukasNeural"],
          sr: ["sr-RS-SophieNeural", "sr-RS-NicholasNeural"],
          sv: ["sv-SE-SofieNeural", "sv-SE-MattiasNeural", "sv-SE-HilleviNeural"],
          ta: ["ta-IN-PallaviNeural", "ta-IN-ValluvarNeural"],
          te: ["te-IN-ShrutiNeural", "te-IN-MohanNeural"],
          th: ["th-TH-PremwadeeNeural", "th-TH-NiwatNeural", "th-TH-AcharaNeural"],
          tr: ["tr-TR-EmelNeural", "tr-TR-AhmetNeural"],
          uk: ["uk-UA-PolinaNeural", "uk-UA-OstapNeural"],
          ur: ["ur-IN-GulNeural", "ur-IN-SalmanNeural"],
          vi: ["vi-VN-HoaiMyNeural", "vi-VN-NamMinhNeural"],
          zh: ["zh-CN-XiaoxiaoNeural", "zh-CN-YunxiNeural", "zh-CN-YunjianNeural", "zh-CN-guangxi-YunqiNeural", "zh-CN-henan-YundengNeural", "zh-CN-liaoning-XiaobeiNeural", "zh-CN-liaoning-YunbiaoNeural", "zh-CN-shaanxi-XiaoniNeural", "zh-CN-shandong-YunxiangNeural", "zh-CN-sichuan-YunxiNeural", "zh-TW-HsiaoChenNeural", "zh-TW-YunJheNeural", "zh-TW-HsiaoYuNeural", "zh-HK-HiuMaanNeural", "zh-HK-WanLungNeural", "zh-HK-HiuGaaiNeural"],
        },
      },
      {
        key: "rate",
        label: "Speaking Rate",
        type: "float",
        required: false,
        default: 0.0,
        min: -50.0,
        max: 50.0,
        step: 1.0,
        description: "Adjust speed in percent offset. 0 = default.",
      },
      {
        key: "pitch",
        label: "Pitch",
        type: "float",
        required: false,
        default: 0.0,
        min: -50.0,
        max: 50.0,
        step: 1.0,
        description: "Adjust pitch in Hz offset. 0 = default.",
      },
    ],
  },

  cartesia: {
    label: "Cartesia",
    fields: [
      {
        key: "model",
        label: "Model",
        type: "select",
        required: true,
        default: "sonic-2",
        language_dependent: true,
        // Docs: https://docs.cartesia.ai/api-reference/tts/models
        options: [
          { value: "sonic-2",           label: "Sonic 2 (multilingual, latest)" },
          { value: "sonic-turbo",       label: "Sonic Turbo (fastest tier)" },
          { value: "sonic-english",     label: "Sonic English (English only)" },
          { value: "sonic-multilingual",label: "Sonic Multilingual v1" },
        ],
        language_map: {
          en: ["sonic-2", "sonic-turbo", "sonic-english", "sonic-multilingual"],
          _default: ["sonic-2", "sonic-turbo", "sonic-multilingual"],
        },
      },
      {
        key: "voice_id",
        label: "Voice",
        type: "select_from_api",
        required: true,
        default: null,
        api_source: "cartesia_voices",
        description: "Voice fetched from your Cartesia account.",
      },
      {
        key: "speed",
        label: "Speed",
        type: "select",
        required: false,
        default: "normal",
        options: [
          { value: "slowest", label: "Slowest" },
          { value: "slow",    label: "Slow" },
          { value: "normal",  label: "Normal" },
          { value: "fast",    label: "Fast" },
          { value: "fastest", label: "Fastest" },
        ],
      },
      {
        key: "emotion",
        label: "Emotion",
        type: "select",
        required: false,
        default: "",
        options: [
          { value: "",                 label: "None (default)" },
          { value: "anger:low",        label: "Anger (low)" },
          { value: "anger:high",       label: "Anger (high)" },
          { value: "positivity:low",   label: "Positivity (low)" },
          { value: "positivity:high",  label: "Positivity (high)" },
          { value: "surprise:low",     label: "Surprise (low)" },
          { value: "surprise:high",    label: "Surprise (high)" },
          { value: "sadness:low",      label: "Sadness (low)" },
          { value: "sadness:high",     label: "Sadness (high)" },
          { value: "curiosity:low",    label: "Curiosity (low)" },
          { value: "curiosity:high",   label: "Curiosity (high)" },
        ],
        description: "Optional emotional tone overlay.",
      },
    ],
  },

  sarvam: {
    label: "Sarvam AI (Bulbul)",
    fields: [
      {
        key: "model",
        label: "Model",
        type: "select",
        required: true,
        default: "bulbul:v2",
        // Docs: https://docs.sarvam.ai/api-reference-docs/text-to-speech/stream
        options: [
          { value: "bulbul:v2", label: "Bulbul v2 (pitch/loudness control)" },
          { value: "bulbul:v3", label: "Bulbul v3 (temperature control)" },
        ],
      },
      {
        key: "voice",
        label: "Speaker",
        type: "select",
        required: true,
        default: "anushka",
        // bulbul:v2 and bulbul:v3 have disjoint speaker sets (see
        // pipecat.services.sarvam.tts.SarvamTTSSpeakerV2/V3) — only offer the
        // set matching the selected model.
        options: [
          { value: "anushka",  label: "Anushka (v2, female)" },
          { value: "manisha",  label: "Manisha (v2, female)" },
          { value: "vidya",    label: "Vidya (v2, female)" },
          { value: "arya",     label: "Arya (v2, female)" },
          { value: "abhilash", label: "Abhilash (v2, male)" },
          { value: "karun",    label: "Karun (v2, male)" },
          { value: "hitesh",   label: "Hitesh (v2, male)" },
          { value: "aditya",   label: "Aditya (v3, male, default)" },
          { value: "shubh",    label: "Shubh (v3, male)" },
          { value: "priya",    label: "Priya (v3, female)" },
          { value: "neha",     label: "Neha (v3, female)" },
        ],
        language_map: {
          "bulbul:v2": ["anushka", "manisha", "vidya", "arya", "abhilash", "karun", "hitesh"],
          "bulbul:v3": ["aditya", "shubh", "priya", "neha"],
        },
      },
      {
        key: "language",
        label: "Target Language",
        type: "select",
        required: true,
        default: "en-IN",
        description: "Sarvam is Indian-language focused (full BCP-47 codes, e.g. hi-IN).",
        options: [
          { value: "en-IN", label: "English (India)" },
          { value: "hi-IN", label: "Hindi" },
          { value: "bn-IN", label: "Bengali" },
          { value: "gu-IN", label: "Gujarati" },
          { value: "kn-IN", label: "Kannada" },
          { value: "ml-IN", label: "Malayalam" },
          { value: "mr-IN", label: "Marathi" },
          { value: "od-IN", label: "Odia" },
          { value: "pa-IN", label: "Punjabi" },
          { value: "ta-IN", label: "Tamil" },
          { value: "te-IN", label: "Telugu" },
        ],
      },
      {
        key: "speed",
        label: "Pace",
        type: "float",
        required: false,
        default: 1.0,
        min: 0.3,
        max: 3.0,
        step: 0.05,
        description: "Speech pace multiplier (bulbul:v2: 0.3-3.0, bulbul:v3: 0.5-2.0 — clamped server-side).",
      },
    ],
  },

  simplismart: {
    label: "Simplismart (Orpheus)",
    fields: [
      {
        key: "model",
        label: "Model",
        type: "select",
        required: true,
        default: "canopylabs/orpheus-3b-0.1-ft",
        // Simplismart-hosted Orpheus via their pipecat plugin
        // (github.com/simpli-smart/pipecat-simplismart). 24kHz PCM streaming HTTP.
        options: [
          { value: "canopylabs/orpheus-3b-0.1-ft", label: "Orpheus 3B (fine-tuned)" },
        ],
      },
      {
        key: "voice",
        label: "Voice",
        type: "select",
        required: true,
        default: "tara",
        // The 8 released Orpheus 3B ft voices (canopylabs); tara is the
        // model's own recommended default (also the plugin default).
        options: [
          { value: "tara", label: "Tara (female, default)" },
          { value: "leah", label: "Leah (female)" },
          { value: "jess", label: "Jess (female)" },
          { value: "mia",  label: "Mia (female)" },
          { value: "zoe",  label: "Zoe (female)" },
          { value: "leo",  label: "Leo (male)" },
          { value: "dan",  label: "Dan (male)" },
          { value: "zac",  label: "Zac (male)" },
        ],
      },
    ],
  },
};

// ── ASR Providers ──────────────────────────────────────────────────────────────

export const ASR_PROVIDERS = {
  deepgram: {
    label: "Deepgram",
    fields: [
      {
        key: "model",
        label: "Model",
        type: "select",
        required: true,
        default: "nova-2",
        language_dependent: true,
        options: [
          { value: "flux-general-en", label: "Flux (real-time turn detection)" },
          { value: "nova-3",         label: "Nova-3 (latest, highest accuracy)" },
          { value: "nova-2",         label: "Nova-2 (multilingual)" },
          { value: "nova-2-general", label: "Nova-2 General" },
          { value: "nova",           label: "Nova (legacy)" },
          { value: "enhanced",       label: "Enhanced" },
          { value: "base",           label: "Base (fastest)" },
        ],
        language_map: {
          en: ["flux-general-en", "nova-3", "nova-2", "nova-2-general", "nova", "enhanced", "base"],
          _default: ["flux-general-en", "nova-3", "nova-2", "nova-2-general"],
        },
      },
      {
        key: "punctuate",
        label: "Punctuation",
        type: "boolean",
        required: false,
        default: true,
        description: "Add punctuation to transcripts.",
      },
      {
        key: "smart_format",
        label: "Smart Format",
        type: "boolean",
        required: false,
        default: true,
        description: "Format numbers, dates, currencies, etc.",
      },
      {
        key: "diarize",
        label: "Speaker Diarization",
        type: "boolean",
        required: false,
        default: false,
        description: "Identify different speakers in the audio.",
      },
      {
        key: "filler_words",
        label: "Include Filler Words",
        type: "boolean",
        required: false,
        default: false,
        description: "Include 'um', 'uh', etc. in transcript.",
      },
    ],
  },
  sarvam: {
    label: "Sarvam AI (Saarika)",
    fields: [
      {
        key: "model",
        label: "Model",
        type: "select",
        required: true,
        default: "saarika:v2.5",
        options: [
          { value: "saarika:v2.5", label: "Saarika v2.5 (latest, auto-detect)" },
          { value: "saarika:v2", label: "Saarika v2" },
        ],
      },
      {
        key: "language",
        label: "Language",
        type: "select",
        required: false,
        default: "unknown",
        description: "Indian-language ASR. 'Auto-detect' lets Saarika detect the language.",
        options: [
          { value: "unknown", label: "Auto-detect" },
          { value: "hi-IN", label: "Hindi" },
          { value: "en-IN", label: "English (India)" },
          { value: "bn-IN", label: "Bengali" },
          { value: "gu-IN", label: "Gujarati" },
          { value: "kn-IN", label: "Kannada" },
          { value: "ml-IN", label: "Malayalam" },
          { value: "mr-IN", label: "Marathi" },
          { value: "od-IN", label: "Odia" },
          { value: "pa-IN", label: "Punjabi" },
          { value: "ta-IN", label: "Tamil" },
          { value: "te-IN", label: "Telugu" },
        ],
      },
    ],
  },

  assemblyai: {
    label: "AssemblyAI",
    fields: [
      {
        key: "model",
        label: "Speech Model",
        type: "select",
        required: true,
        default: "best",
        options: [
          { value: "best", label: "Best (highest accuracy)" },
          { value: "nano", label: "Nano (fastest, lower cost)" },
        ],
      },
      {
        key: "language_code",
        label: "Language",
        type: "select",
        required: false,
        default: "en",
        language_dependent: true,
        options: [
          { value: "en", label: "English" },
          { value: "es", label: "Spanish" },
          { value: "fr", label: "French" },
          { value: "de", label: "German" },
          { value: "it", label: "Italian" },
          { value: "pt", label: "Portuguese" },
          { value: "nl", label: "Dutch" },
          { value: "ja", label: "Japanese" },
          { value: "ko", label: "Korean" },
          { value: "zh", label: "Chinese" },
          { value: "fi", label: "Finnish" },
          { value: "ru", label: "Russian" },
          { value: "tr", label: "Turkish" },
          { value: "pl", label: "Polish" },
        ],
      },
      {
        key: "speaker_labels",
        label: "Speaker Labels",
        type: "boolean",
        required: false,
        default: false,
        description: "Label each speaker in the transcript.",
      },
      {
        key: "filter_profanity",
        label: "Filter Profanity",
        type: "boolean",
        required: false,
        default: false,
      },
    ],
  },

  openai: {
    label: "OpenAI Whisper",
    fields: [
      {
        key: "model",
        label: "Model",
        type: "select",
        required: true,
        default: "whisper-1",
        options: [{ value: "whisper-1", label: "Whisper v1 (multilingual)" }],
      },
      {
        key: "language",
        label: "Language Hint",
        type: "select",
        required: false,
        default: "",
        language_dependent: true,
        description: "Providing the language improves accuracy and speed.",
        options: [
          { value: "",   label: "Auto-detect" },
          { value: "en", label: "English" },
          { value: "es", label: "Spanish" },
          { value: "fr", label: "French" },
          { value: "de", label: "German" },
          { value: "it", label: "Italian" },
          { value: "pt", label: "Portuguese" },
          { value: "nl", label: "Dutch" },
          { value: "ja", label: "Japanese" },
          { value: "ko", label: "Korean" },
          { value: "zh", label: "Chinese" },
          { value: "ar", label: "Arabic" },
          { value: "hi", label: "Hindi" },
          { value: "tr", label: "Turkish" },
          { value: "ru", label: "Russian" },
        ],
      },
    ],
  },
};

// ── Defaults ───────────────────────────────────────────────────────────────────

export const PROVIDER_DEFAULTS = {
  // Mirrors ai_config.yml's test_caller_bot block and the agents table column
  // defaults. The form used to send gpt-4.1 on every new agent, which — like
  // tts_provider below — silently overrode the backend's gemini default.
  llm_provider: "openai",
  llm_model:    "gpt-4o-mini",
  llm_config:   { temperature: 0.7, max_tokens: 150 },
  // Mirrors the backend default (AiConfigService.test_caller_bot_defaults + the
  // agents.tts_provider column default): OpenAI gpt-4o-mini-tts, voice "alloy".
  // OpenAI TTS is multilingual (the voice follows the text's language). This MUST
  // stay in sync with the backend: for a normal user AgentsController strips
  // tts_provider/tts_config and the column default applies, but for an INTERNAL
  // ADMIN the form's value is honored — so a stale value here creates admin agents
  // on the wrong provider.
  tts_provider: "openai",
  tts_config:   { model: "gpt-4o-mini-tts", voice: "alloy", speed: 1.0 },
  asr_provider: "deepgram",
  asr_config:   { model: "nova-3-general", punctuate: true, smart_format: true },
};

// ── LiteLLM model options (for DSPy/GEPA tasks: optimization, generation, evaluation) ──
// These use the "provider/model" format expected by the Python tools backend.

export const DEFAULT_LITELLM_MODEL = "openai/gpt-4.1";

export const LITELLM_MODEL_OPTIONS = [
  { value: "openai/gpt-4.1",              label: "GPT-4.1 (OpenAI)" },
  { value: "openai/gpt-4.1-mini",         label: "GPT-4.1 Mini (OpenAI)" },
  { value: "anthropic/claude-sonnet-4-6",  label: "Claude Sonnet 4.6" },
  { value: "gemini/gemini-2.5-flash",      label: "Gemini 2.5 Flash" },
];
